import React, { memo } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';

export interface IFilterCrumb {
  /** Стабильный ключ строки — он же то, что снимает ✕. */
  key: string;
  /** Название ограничения: «Группа», «Исполнители». */
  label: string;
  /** Читаемое значение: «WEB-24», «Иванов, Петрова». */
  value: string;
  /** Не передан — крошка показывается, но не снимается. */
  onClear?: () => void;
}

interface IFilterCrumbsProps {
  crumbs: IFilterCrumb[];
  onClearAll?: () => void;
  clearAllLabel: string;
  /** Доступное имя для каждой ✕ (страница подставляет свой перевод). */
  clearOneLabel: string;
  /** Одна строка с прокруткой вместо переноса — см. `Row`. */
  scroll?: boolean;
}

/**
 * Что именно сузило список — и способ отменить ОДНУ его часть.
 *
 * Поднято из аналитики опросов (ТЗ 0062), когда появился второй потребитель.
 * Молекула владеет только внешним видом: подписи и сопоставление
 * «поле → человеческое имя» остаются у страницы, потому что они её домен.
 *
 * Смысл, ради которого это существует: экран, сузившийся без объяснения,
 * читается как поломка. Общая кнопка «сбросить всё» этого не лечит — она
 * снимает разом и то, что нужно было оставить.
 */
export const FilterCrumbs = memo(
  ({
    crumbs,
    onClearAll,
    clearAllLabel,
    clearOneLabel,
    scroll = false,
  }: IFilterCrumbsProps) => {
    if (!crumbs.length) return null;
    return (
      <Row $scroll={scroll}>
        {crumbs.map((crumb) => (
          <Crumb key={crumb.key}>
            <CrumbLabel>{crumb.label}:</CrumbLabel>
            <CrumbValue title={crumb.value}>{crumb.value}</CrumbValue>
            {crumb.onClear ? (
              <Remove
                type="button"
                aria-label={`${clearOneLabel}: ${crumb.label}`}
                onClick={crumb.onClear}
              >
                <X size={12} aria-hidden="true" />
              </Remove>
            ) : null}
          </Crumb>
        ))}
        {onClearAll && crumbs.length > 1 ? (
          <ClearAll type="button" onClick={onClearAll}>
            {clearAllLabel}
          </ClearAll>
        ) : null}
      </Row>
    );
  },
);

FilterCrumbs.displayName = 'FilterCrumbs';

/** `scroll` — одна строка с горизонтальной прокруткой вместо переноса.
 *
 *  Нужен там, где крошек может стать много: на доске задач их по одной на
 *  каждый вид связи, и при трёх видах лента уезжала во второй, а то и третий
 *  ряд по 33px — отгрызая место, отвоёванное у шапки. Прокрутка честнее
 *  переноса: крошки остаются все, но полотно задач не двигается. */
const Row = styled.div<{ $scroll?: boolean }>`
  display: flex;
  flex-wrap: ${({ $scroll }) => ($scroll ? 'nowrap' : 'wrap')};
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  ${({ $scroll }) =>
    $scroll &&
    `
    overflow-x: auto;
    scrollbar-width: thin;
    &::-webkit-scrollbar { height: 4px; }
  `}
`;

const Crumb = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 320px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 14px;
  padding: 3px 6px 3px 10px;
  font-size: 12px;
  color: #1e3a8a;
`;

const CrumbLabel = styled.span`
  opacity: 0.7;
`;

const CrumbValue = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
`;

const Remove = styled.button`
  display: inline-flex;
  align-items: center;
  border: none;
  background: transparent;
  padding: 0;
  color: #1e3a8a;
  cursor: pointer;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`;

const ClearAll = styled.button`
  border: none;
  background: transparent;
  padding: 0 4px;
  color: #6b7280;
  cursor: pointer;
  font-size: 12px;
`;
